import { ref } from 'vue';
import { httpService } from '../services/httpService';
import { log } from '../utils/DebugLogger';
import { useDateFormatter } from './useDateFormatter';
import { validate } from '../contracts/validate';
import '../contracts/Payroll_CreateEmployeeAdvanceInput.ts';
import '../contracts/Payroll_UpdateEmployeeAdvanceInput.ts';
import '../contracts/Payroll_CreateCashAdvanceInput.ts';
import '../contracts/Payroll_CreateCashAdvancesBatchInput.ts';
import '../contracts/Payroll_ProcessDiscountsInput.ts';
import '../contracts/Payroll_CancelEmployeeAdvanceInput';
import '../contracts/Payroll_ReopenInput.ts';
import '../contracts/Payroll_ReopenEmployeeAdvanceResponse.ts';
import '../contracts/Payroll_AvailableCashRecebimentosResponse.ts';
import '../contracts/Payroll_AvailableCashRecebimentosResponse';
import '../contracts/Payroll_CancelEmployeeAdvanceResponse';
import '../contracts/Payroll_CreateEmployeeAdvanceResponse';
import '../contracts/Payroll_DeleteEmployeeAdvanceResponse';
import '../contracts/Payroll_EmployeeAdvanceResponse';
import '../contracts/Payroll_ReopenEmployeeAdvanceResponse';
import '../contracts/Payroll_UpdateEmployeeAdvanceResponse';

import '../contracts/Payroll_CreateCashAdvanceResponse';
import '../contracts/Payroll_CreateCashAdvancesBatchResponse';
import '../contracts/Payroll_OpenAdvancesForPeriodResponse';
import '../contracts/Payroll_ProcessAdvanceDiscountsResponse';
import '../contracts/Payroll_EmployeeAdvancesResponse';
import type { Payroll_CreateCashAdvanceInput } from '../contracts/Payroll_CreateCashAdvanceInput';
import type { Payroll_CreateCashAdvancesBatchInput } from '../contracts/Payroll_CreateCashAdvancesBatchInput';
import type { Payroll_CreateEmployeeAdvanceInput } from '../contracts/Payroll_CreateEmployeeAdvanceInput';
import type { Payroll_ProcessDiscountsInput } from '../contracts/Payroll_ProcessDiscountsInput';
import type { Payroll_ReopenInput } from '../contracts/Payroll_ReopenInput';
import type { Payroll_UpdateEmployeeAdvanceInput } from '../contracts/Payroll_UpdateEmployeeAdvanceInput';
/**
 * Status constants (mirror backend)
 */
export const ADVANCE_STATUS = {
  OPEN: 0,
  PARTIAL: 1,
  PAID: 2,
  CANCELED: -1
};

export const ADVANCE_STATUS_LABELS = {
  0: 'Aberto',
  1: 'Parcial',
  2: 'Pago',
  '-1': 'Cancelado'
};

export const ADVANCE_STATUS_SEVERITY = {
  0: 'info',      // Blue
  1: 'warning',   // Orange/Yellow
  2: 'success',   // Green
  '-1': 'danger'  // Red
};

// NEW: Source type constants
export const ADVANCE_SOURCE_TYPE = {
  MANUAL: 'manual',
  CASH_RECEBIMENTO: 'cash_recebimento'
};

export const ADVANCE_SOURCE_TYPE_LABELS = {
  manual: 'Manual',
  cash_recebimento: 'Dinheiro'
};

export const ADVANCE_SOURCE_TYPE_SEVERITY = {
  manual: 'info',
  cash_recebimento: 'success'
};

/**
 * Composable for managing employee advances
 *
 * NEW design - independent of payroll period:
 * - Advances have status: open, partial, paid, canceled
 * - Advances are linked to payroll via payroll_advance_items
 * - HAS expense relationship (despesa created immediately when advance is given)
 */
export function useEmployeeAdvances() {
  const isLoading = ref(false);
  const error = ref(null);

  const { fromApiDateToDate, fromDateToApiDate } = useDateFormatter();

  /**
   * Transform API response to typed data
   */
  const transformAdvance = (data: any) => {
    const sourceType = data.source_type || ADVANCE_SOURCE_TYPE.MANUAL;

    return {
      ...data,
      // Convert date-only field (no timezone issues)
      request_date: data.request_date ? fromApiDateToDate(data.request_date) : null,
      // Keep original for form
      request_date_string: data.request_date,
      // Parse amounts
      amount: parseFloat(data.amount || 0),
      amount_paid: parseFloat(data.amount_paid || 0),
      amount_remaining: parseFloat(data.amount || 0) - parseFloat(data.amount_paid || 0),
      // Status helpers
      status_label: ADVANCE_STATUS_LABELS[data.status as keyof typeof ADVANCE_STATUS_LABELS] || 'Desconhecido',
      status_severity: ADVANCE_STATUS_SEVERITY[data.status as keyof typeof ADVANCE_STATUS_SEVERITY] || 'info',
      // NEW: Source type helpers
      source_type: sourceType,
      source_type_label: data.source_type_label || ADVANCE_SOURCE_TYPE_LABELS[sourceType as keyof typeof ADVANCE_SOURCE_TYPE_LABELS] || 'Manual',
      source_type_severity: ADVANCE_SOURCE_TYPE_SEVERITY[sourceType as keyof typeof ADVANCE_SOURCE_TYPE_SEVERITY] || 'info',
      // Related fields for cash advances
      recebimento_id: data.recebimento_id || null,
      origin_period_id: data.origin_period_id || null,
      cash_date: data.cash_date ? fromApiDateToDate(data.cash_date) : null,
      cash_cliente_nome: data.cash_cliente_nome || null
    };
  };

  /**
   * List advances with filters
   */
  const listAdvances = async (filters: any = {}) => {
    isLoading.value = true;
    error.value = null;

    try {
      const params = new URLSearchParams();
      if (filters.franchise_id) params.append('franchise_id', filters.franchise_id);
      if (filters.employee_id) params.append('employee_id', filters.employee_id);
      if (filters.status !== undefined && filters.status !== null) params.append('status', filters.status);
      if (filters.date_from) params.append('date_from', filters.date_from);
      if (filters.date_to) params.append('date_to', filters.date_to);
      if (filters.open_or_partial) params.append('open_or_partial', '1');

      const queryString = params.toString();
      const url = `/payroll/employee_advances${queryString ? '?' + queryString : ''}`;
      log('useEmployeeAdvances', 'listAdvances', 'Fetching', url);

      const response = await httpService.get(url, {
        responseContract: 'Payroll_EmployeeAdvancesResponse'
      });

      if (response.success) {
        const advances = Array.isArray(response.dados) ? response.dados : [];
        log('useEmployeeAdvances', 'listAdvances', 'Success', { count: advances.length });
        return advances.map(transformAdvance);
      } else {
        throw new Error(response.error || 'Falha ao buscar adiantamentos');
      }
    } catch (err) {
      error.value = err.message;
      log('useEmployeeAdvances', 'listAdvances', 'Error', err);
      return [];
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Get single advance by ID
   */
  const getAdvance = async (id: any) => {
    isLoading.value = true;
    error.value = null;

    try {
      const response = await httpService.get(`/payroll/employee_advance/${id}`, {
        responseContract: 'Payroll_EmployeeAdvanceResponse'
      });

      if (response.success && response.dados) {
        return transformAdvance(response.dados);
      } else {
        throw new Error(response.error || 'Adiantamento não encontrado');
      }
    } catch (err) {
      error.value = err.message;
      return null;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Create a new advance
   */
  const createAdvance = async (data: any) => {
    isLoading.value = true;
    error.value = null;

    try {
      const payload: Payroll_CreateEmployeeAdvanceInput = {
        employee_id: data.employee_id,
        franchise_id: data.franchise_id,
        title: data.title,
        description: data.description || null,
        amount: data.amount,
        // Convert Date to API format (date-only, no UTC conversion)
        request_date: data.request_date instanceof Date
          ? fromDateToApiDate(data.request_date)
          : data.request_date
      };

      log('useEmployeeAdvances', 'createAdvance', 'Creating', payload);

      validate('Payroll_CreateEmployeeAdvanceInput', payload);
      const response = await httpService.post('/payroll/create_employee_advance', payload, {
        inputContract: 'Payroll_CreateEmployeeAdvanceInput',
        responseContract: 'Payroll_CreateEmployeeAdvanceResponse'
      });

      if (response.success) {
        if (!response.dados) {
          throw new Error(response.error || 'Empty create advance response');
        }
        return transformAdvance(response.dados);
      } else {
        throw new Error(response.error || 'Falha ao criar adiantamento');
      }
    } catch (err) {
      error.value = err.message;
      return null;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Update an existing advance (only if open)
   */
  const updateAdvance = async (id: any, data: any) => {
    isLoading.value = true;
    error.value = null;

    try {
      const payload: Payroll_UpdateEmployeeAdvanceInput = {};
      if (data.title !== undefined) payload.title = data.title;
      if (data.description !== undefined) payload.description = data.description;
      if (data.amount !== undefined) payload.amount = data.amount;
      if (data.request_date !== undefined) {
        payload.request_date = data.request_date instanceof Date
          ? fromDateToApiDate(data.request_date)
          : data.request_date;
      }

      validate('Payroll_UpdateEmployeeAdvanceInput', payload);
      const response = await httpService.put(`/payroll/update_employee_advance/${id}`, payload, {
        inputContract: 'Payroll_UpdateEmployeeAdvanceInput',
        responseContract: 'Payroll_UpdateEmployeeAdvanceResponse'
      });

      if (response.success) {
        if (!response.dados) {
          throw new Error(response.error || 'Empty update advance response');
        }
        return transformAdvance(response.dados);
      } else {
        throw new Error(response.error || 'Falha ao atualizar adiantamento');
      }
    } catch (err) {
      error.value = err.message;
      return null;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Delete an advance (only if open)
   */
  const deleteAdvance = async (id: any) => {
    isLoading.value = true;
    error.value = null;

    try {
      const response = await httpService.delete(`/payroll/delete_employee_advance/${id}`, {
        responseContract: 'Payroll_DeleteEmployeeAdvanceResponse'
      });
      return response.success;
    } catch (err) {
      error.value = err.message;
      return false;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Cancel an advance
   */
  const cancelAdvance = async (id: number) => {
    isLoading.value = true;
    error.value = null;

    try {
      const response = await httpService.post(`/payroll/cancel_employee_advance/${id}`, null, {
        inputContract: 'Payroll_CancelEmployeeAdvanceInput',
        responseContract: 'Payroll_CancelEmployeeAdvanceResponse'
      });

      if (response.success) {
        if (!response.dados) {
          throw new Error(response.error || 'Empty cancel advance response');
        }
        return { id: response.dados.id, status: response.dados.status, status_label: response.dados.status_label };
      } else {
        throw new Error(response.error || 'Falha ao cancelar adiantamento');
      }
    } catch (err) {
      error.value = err.message;
      return null;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Reopen an advance (from canceled or paid status)
   */
  const reopenAdvance = async (id: any, targetStatus: string | null = null) => {
    isLoading.value = true;
    error.value = null;

    try {
      const payload: Payroll_ReopenInput = targetStatus ? { target_status: targetStatus } : {};

      validate('Payroll_ReopenInput', payload);
      const response = await httpService.post(`/payroll/reopen_employee_advance/${id}`, payload, {
        inputContract: 'Payroll_ReopenInput',
        responseContract: 'Payroll_ReopenEmployeeAdvanceResponse'
      });

      if (response.success) {
        if (!response.dados) {
          throw new Error(response.error || 'Empty reopen advance response');
        }
        return { id: response.dados.id, status: response.dados.status, status_label: response.dados.status_label };
      } else {
        throw new Error(response.error || 'Falha ao reabrir adiantamento');
      }
    } catch (err) {
      error.value = err.message;
      return null;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Get open/partial advances for a payroll period (wizard)
   */
  const getOpenAdvancesForPeriod = async (periodId: number) => {
    isLoading.value = true;
    error.value = null;

    log('useEmployeeAdvances', 'getOpenAdvancesForPeriod', '📡 START - Calling API', {
      periodId: periodId,
      endpoint: `/payroll/open_advances_for_period/${periodId}`
    });

    try {
      const response = await httpService.get(`/payroll/open_advances_for_period/${periodId}`, { responseContract: 'Payroll_OpenAdvancesForPeriodResponse' });

      log('useEmployeeAdvances', 'getOpenAdvancesForPeriod', '📦 Raw API response', {
        success: response.success,
        hasData: !!response.dados,
        rawAdvancesCount: response.dados?.advances?.length || 0,
        rawAdvanceIds: response.dados?.advances?.map((a: any) => a.id) || [],
        rawTotalOpen: response.dados?.total_open,
        rawCashCount: response.dados?.available_cash_recebimentos?.length || 0
      });

      if (response.success && response.dados) {
        const data = response.dados;
        const transformedAdvances = (data.advances || []).map(transformAdvance);

        const result = {
          advances: transformedAdvances,
          summary_by_employee: data.summary_by_employee || [],
          total_open: data.total_open || 0,
          // NEW: Include available cash recebimentos
          available_cash_recebimentos: data.available_cash_recebimentos || [],
          // NEW: Include already-selected items for this period (for resuming/editing)
          selected_items: data.selected_items || []
        };

        log('useEmployeeAdvances', 'getOpenAdvancesForPeriod', '✅ END - Returning transformed data', {
          advancesCount: result.advances.length,
          advanceDetails: result.advances.map((a: any) => ({
            id: a.id,
            idType: typeof a.id,
            title: a.title,
            amount_remaining: a.amount_remaining,
            status: a.status
          })),
          totalOpen: result.total_open,
          summaryByEmployeeCount: result.summary_by_employee.length,
          cashRecebimentosCount: result.available_cash_recebimentos.length,
          selectedItemsCount: result.selected_items.length,
          selectedItemsDetails: result.selected_items.map((item: any) => ({
            advance_id: item.advance_id,
            amount: item.amount,
            title: item.title
          }))
        });

        return result;
      } else {
        throw new Error(response.error || 'Falha ao buscar adiantamentos abertos');
      }
    } catch (err) {
      error.value = err.message;
      log('useEmployeeAdvances', 'getOpenAdvancesForPeriod', '❌ Error', err);
      return { advances: [], summary_by_employee: [], total_open: 0, available_cash_recebimentos: [], selected_items: [] };
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Get available cash recebimentos for a period
   */
  const fetchAvailableCashRecebimentos = async (periodId: number) => {
    isLoading.value = true;
    error.value = null;

    try {
      const response = await httpService.get(`/payroll/available_cash_recebimentos/${periodId}`, {
        responseContract: 'Payroll_AvailableCashRecebimentosResponse'
      });

      if (response.success) {
        return response.dados || [];
      } else {
        throw new Error(response.error || 'Falha ao buscar recebimentos em dinheiro');
      }
    } catch (err) {
      error.value = err.message;
      return [];
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Create a cash advance from recebimento
   */
  const createCashAdvance = async (periodId: any, recebimentoId: any, franchiseId: any) => {
    isLoading.value = true;
    error.value = null;

    try {
      const payload: Payroll_CreateCashAdvanceInput = {
        period_id: periodId,
        recebimento_id: recebimentoId,
        franchise_id: franchiseId
      };

      log('useEmployeeAdvances', 'createCashAdvance', 'Creating', payload);

      validate('Payroll_CreateCashAdvanceInput', payload);
      const response = await httpService.post('/payroll/create_cash_advance', payload, { responseContract: 'Payroll_CreateCashAdvanceResponse' });

      if (response.success && response.dados) {
        return response.dados.advance_id;
      } else {
        throw new Error(response.error || 'Falha ao criar adiantamento de dinheiro');
      }
    } catch (err) {
      error.value = err.message;
      return null;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Create multiple cash advances at once
   */
  const createCashAdvancesBatch = async (periodId: any, recebimentoIds: any, franchiseId: any) => {
    isLoading.value = true;
    error.value = null;

    try {
      const payload: Payroll_CreateCashAdvancesBatchInput = {
        period_id: periodId,
        recebimento_ids: recebimentoIds,
        franchise_id: franchiseId
      };

      log('useEmployeeAdvances', 'createCashAdvancesBatch', 'Creating batch', payload);

      validate('Payroll_CreateCashAdvancesBatchInput', payload);
      const response = await httpService.post('/payroll/create_cash_advances_batch', payload, { responseContract: 'Payroll_CreateCashAdvancesBatchResponse' });

      if (response.success) {
        return response.dados || { created: [], errors: [], created_count: 0, error_count: 0 };
      } else {
        throw new Error(response.error || 'Falha ao criar adiantamentos em lote');
      }
    } catch (err) {
      error.value = err.message;
      return null;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Process advance discounts (wizard confirmation)
   */
  const processDiscounts = async (periodId: any, discounts: any) => {
    isLoading.value = true;
    error.value = null;

    try {
      const payload: Payroll_ProcessDiscountsInput = {
        period_id: periodId,
        discounts: discounts.map((d: any) => ({
          advance_id: d.advance_id,
          amount: d.amount
        }))
      };

      validate('Payroll_ProcessDiscountsInput', payload);
      const response = await httpService.post('/payroll/process_advance_discounts', payload, { responseContract: 'Payroll_ProcessAdvanceDiscountsResponse' });

      if (response.success) {
        return response.dados;
      } else {
        throw new Error(response.error || 'Falha ao processar descontos');
      }
    } catch (err) {
      error.value = err.message;
      return null;
    } finally {
      isLoading.value = false;
    }
  };

  return {
    // State
    isLoading,
    error,

    // Constants
    ADVANCE_STATUS,
    ADVANCE_STATUS_LABELS,
    ADVANCE_STATUS_SEVERITY,
    ADVANCE_SOURCE_TYPE,
    ADVANCE_SOURCE_TYPE_LABELS,
    ADVANCE_SOURCE_TYPE_SEVERITY,

    // Methods
    listAdvances,
    getAdvance,
    createAdvance,
    updateAdvance,
    deleteAdvance,
    cancelAdvance,
    reopenAdvance,
    getOpenAdvancesForPeriod,
    fetchAvailableCashRecebimentos,
    createCashAdvance,
    createCashAdvancesBatch,
    processDiscounts
  };
}
